import React, {
  memo,
  useCallback,
  useEffect,
  useState,
  useMemo,
  useImperativeHandle,
  forwardRef,
  useRef,
} from 'react';
import { Box, Grid, MenuItem } from '@mui/material';
import { useFormik } from 'formik';
import { useListingColors } from 'legacyfrontend/hooks/useListingColors';
import FormTextField from './components/FormTextField';
import FormSelectField from './components/FormSelectField';
import FormDateField from './components/FormDateField';
import FormCheckbox from './components/FormCheckbox';
import FormRadioGroup from './components/FormRadioGroup';
import { generatePassengerSchema } from './components/validationSchema';
import EmergencyContactSection from './components/EmergencyContactSection';
import FiscalDataSection from './components/FiscalDataSection';
import { COUNTRIES } from 'legacyfrontend/constants/countries';
import * as Yup from 'yup';

const PassengerForm = forwardRef(
  (
    {
      passenger,
      index,
      totalPassengers,
      useCommonFiscalData,
      commonFiscalDataPassengerIndex,
      commonFiscalData,
      passengersFiscalData,
      handleCommonFiscalDataToggle,
      handleCommonFiscalDataChange,
      handleIndividualFiscalDataChange,
      updatePassengerInfo,
      updateEmergencyContact,
      updateFiscalData,
      passengerConfig,
      isInternational,
      flightInfo,
    },
    ref
  ) => {
    const { background } = useListingColors();
    const [showEmergencyContact, setShowEmergencyContact] = useState(false);
    const emergencyContactRef = useRef(null);

    const passengerType = useMemo(() => {
      if (!passenger) return 'ADT';
      if (passenger.type === 'Adulto') return 'ADT';
      if (passenger.type === 'Niño') return 'CHD';
      if (passenger.type === 'Bebé') return 'INF';
      return 'ADT';
    }, [passenger]);

    const validationSchema = useMemo(() => {
      return generatePassengerSchema(passengerConfig, passengerType, passenger?.isAdult);
    }, [passengerConfig, passengerType, passenger?.isAdult]);

    const availableFields = useMemo(() => {
      if (!passengerConfig?.passenger?.fields) return {};
      const fields = passengerConfig.passenger.fields[passengerType] || [];
      return fields.reduce((acc, field) => {
        acc[field.name] = {
          mandatory: field.mandatory,
          editable: field.editable,
          enums: field.enums,
        };
        return acc;
      }, {});
    }, [passengerConfig, passengerType]);

    const requiredFields = useMemo(() => {
      return passengerConfig?.passenger?.requiredFields?.[passengerType] || [];
    }, [passengerConfig, passengerType]);

    const hasFrequentFlyer = useMemo(
      () => 'frequent_traveller' in availableFields,
      [availableFields]
    );
    const hasIssueDate = useMemo(() => 'issue_date' in availableFields, [availableFields]);
    const hasIssuedIn = useMemo(() => 'issued_in' in availableFields, [availableFields]);
    const hasNationality = useMemo(() => 'nationality' in availableFields, [availableFields]);
    const hasExpiryDate = useMemo(() => 'expiry_date' in availableFields, [availableFields]);

    const formik = useFormik({
      initialValues: {
        gender: passenger?.gender || '',
        name: passenger?.name || '',
        lastName: passenger?.lastName || '',
        frequentFlyer: passenger?.frequentFlyer || '',
        birthDate: passenger?.birthDate || '',
        documentType: passenger?.documentType || '',
        documentNumber: passenger?.documentNumber || '',
        issueDate: passenger?.issueDate || '',
        issuedIn: passenger?.issuedIn || '',
        nationality: passenger?.nationality || '',
        expiryDate: passenger?.expiryDate || '',
        passengerType: passenger?.type || '',
      },
      validationSchema,
      validateOnMount: false,
      validateOnChange: false,
      validateOnBlur: true,
      onSubmit: (values) => {
        updatePassengerInfo(index, values, Object.keys(formik.errors).length > 0);
      },
    });

    useImperativeHandle(ref, () => ({
      validate: async () => {
        Object.keys(formik.values).forEach((field) => {
          formik.setFieldTouched(field, true);
        });

        const errors = await formik.validateForm();

        let hasRequiredFieldErrors = Object.keys(errors).some(
          (field) =>
            (requiredFields.includes(field) ||
              field === 'documentType' ||
              field === 'documentNumber') &&
            field !== 'emergencyContact'
        );

        if (showEmergencyContact && emergencyContactRef.current) {
          const isEmergencyContactValid = await emergencyContactRef.current.validate();
          if (!isEmergencyContactValid) {
            hasRequiredFieldErrors = true;
          }
        }

        updatePassengerInfo(index, formik.values, hasRequiredFieldErrors);
        return !hasRequiredFieldErrors;
      },
      getValues: () => formik.values,
      getErrors: () => formik.errors,
      getFieldLabels: () => ({
        gender: 'Género',
        name: 'Nombre',
        lastName: 'Apellidos',
        birthDate: 'Fecha de nacimiento',
        documentType: 'Tipo de documento',
        documentNumber: 'Número de documento',
      }),
    }));

    useEffect(() => {
      if (formik.values && Object.keys(formik.values).length > 0) {
        const hasRequiredFieldErrors = Object.keys(formik.errors).some(
          (field) =>
            (requiredFields.includes(field) ||
              field === 'documentType' ||
              field === 'documentNumber') &&
            field !== 'emergencyContact'
        );
        updatePassengerInfo(index, formik.values, hasRequiredFieldErrors);
      }
    }, [formik.values, formik.errors, index, updatePassengerInfo, requiredFields]);

    useEffect(() => {
      if (showEmergencyContact) {
        formik.setFieldValue('emergencyContact.hasEmergencyContact', true);
      }
    }, [showEmergencyContact]);

    const handleEmergencyContactChange = useCallback((e) => {
      setShowEmergencyContact(e.target.checked);
    }, []);

    const shouldShowError = useCallback(
      (fieldName) => {
        const isRequired = requiredFields.includes(fieldName);
        const isEmpty =
          !formik.values[fieldName] ||
          (typeof formik.values[fieldName] === 'string' && formik.values[fieldName].trim() === '');

        // Para el tipo de documento y número de documento, mostrar error si está tocado y está vacío o tiene error
        if (fieldName === 'documentType' || fieldName === 'documentNumber') {
          return formik.touched[fieldName] && (isEmpty || Boolean(formik.errors[fieldName]));
        }

        return (
          (formik.touched[fieldName] || formik.submitCount > 0) &&
          (Boolean(formik.errors[fieldName]) || (isRequired && isEmpty))
        );
      },
      [formik.values, formik.touched, formik.submitCount, formik.errors, requiredFields]
    );

    const getErrorMessage = useCallback(
      (fieldName) => {
        if (shouldShowError(fieldName)) {
          if (formik.errors[fieldName]) {
            return formik.errors[fieldName];
          }
          return 'Este campo es requerido';
        }
        return '';
      },
      [shouldShowError, formik.errors]
    );

    const handleBlur = useCallback(
      (e) => {
        const { name } = e.target;
        formik.handleBlur(e);
        formik.validateField(name);
      },
      [formik]
    );

    const handleChange = useCallback(
      (e) => {
        const { name, value } = e.target;

        if (name === 'documentType') {
          const currentDocumentNumber = formik.values.documentNumber;
          formik.setFieldValue(name, value, true);
          formik.setFieldValue('documentNumber', currentDocumentNumber, true);
        } else {
          formik.setFieldValue(name, value);
        }
      },
      [formik]
    );

    return (
      <Box sx={{ px: { xs: 0, sm: 3 }, py: 3, pt: 1, bgcolor: 'white', borderRadius: 1 }}>
        <Grid item sx={{ mb: 2 }} xs={12}>
          <FormRadioGroup
            background={background}
            error={shouldShowError('gender')}
            formik={formik}
            helperText={getErrorMessage('gender')}
            name='gender'
            required
          />
        </Grid>

        <Grid container spacing={2}>
          <Grid item md={4} xs={12}>
            <FormTextField
              error={shouldShowError('name')}
              formik={formik}
              helperText={getErrorMessage('name')}
              inputType='text'
              label='Nombre'
              name='name'
              onBlur={handleBlur}
              onChange={handleChange}
              placeholder='Ingrese su nombre'
              required
            />
          </Grid>

          <Grid item md={8} xs={12}>
            <FormTextField
              error={shouldShowError('lastName')}
              formik={formik}
              helperText={
                <>
                  {41 - (formik.values.lastName?.length || 0)} caracteres restantes
                  {shouldShowError('lastName') && (
                    <span style={{ color: 'error.main', display: 'block' }}>
                      {getErrorMessage('lastName')}
                    </span>
                  )}
                </>
              }
              inputProps={{ maxLength: 41 }}
              inputType='text'
              label='Apellidos'
              name='lastName'
              onBlur={handleBlur}
              onChange={handleChange}
              placeholder='Ingrese sus apellidos'
              required
            />
          </Grid>

          <Grid item md={4} xs={12}>
            <FormDateField
              error={shouldShowError('birthDate')}
              formik={formik}
              helperText={getErrorMessage('birthDate')}
              label='Fecha de nacimiento'
              maxDate={new Date()}
              name='birthDate'
              onBlur={handleBlur}
              onChange={handleChange}
              required
            />
          </Grid>

          <Grid item md={4} xs={12}>
            <FormSelectField
              error={shouldShowError('documentType')}
              formik={formik}
              helperText={getErrorMessage('documentType')}
              label='Tipo de documento'
              name='documentType'
              onBlur={handleBlur}
              onChange={handleChange}
              required
            >
              <MenuItem value='documento_identidad'>Documento de identidad</MenuItem>
              <MenuItem value='pasaporte'>Pasaporte</MenuItem>
            </FormSelectField>
          </Grid>

          <Grid item md={4} xs={12}>
            <FormTextField
              error={shouldShowError('documentNumber')}
              formik={formik}
              helperText={getErrorMessage('documentNumber')}
              inputType={formik.values.documentType === 'pasaporte' ? 'alphanumeric' : 'numeric'}
              label='N° de documento'
              name='documentNumber'
              onBlur={handleBlur}
              onChange={handleChange}
              placeholder={
                formik.values.documentType === 'documento_identidad' ? '12345678' : 'AB123456'
              }
              required
            />
          </Grid>

          {hasFrequentFlyer && (
            <Grid item md={4} xs={12}>
              <FormTextField
                formik={formik}
                inputType='numeric'
                label='Pasajero frecuente'
                name='frequentFlyer'
                onBlur={handleBlur}
                onChange={handleChange}
                placeholder='123456789'
                required={availableFields['frequent_traveller']?.mandatory}
              />
            </Grid>
          )}

          {hasIssueDate && (
            <Grid item md={4} xs={12}>
              <FormDateField
                formik={formik}
                label='Fecha Expedición'
                name='issueDate'
                onBlur={handleBlur}
                onChange={handleChange}
                required={availableFields['issue_date']?.mandatory}
              />
            </Grid>
          )}

          {hasIssuedIn && (
            <Grid item md={4} xs={12}>
              <FormSelectField
                formik={formik}
                label='Documento Expedido en'
                name='issuedIn'
                onBlur={handleBlur}
                onChange={handleChange}
                required={availableFields['issued_in']?.mandatory}
              >
                {COUNTRIES.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.name}
                  </MenuItem>
                ))}
              </FormSelectField>
            </Grid>
          )}

          {hasNationality && (
            <Grid item md={4} xs={12}>
              <FormSelectField
                formik={formik}
                label='Nacionalidad'
                name='nationality'
                onBlur={handleBlur}
                onChange={handleChange}
                required={availableFields['nationality']?.mandatory}
              >
                {COUNTRIES.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.name}
                  </MenuItem>
                ))}
              </FormSelectField>
            </Grid>
          )}

          {hasExpiryDate && (
            <Grid item md={4} xs={12}>
              <FormDateField
                formik={formik}
                label='Fecha Caducidad'
                name='expiryDate'
                onBlur={handleBlur}
                onChange={handleChange}
                required={availableFields['expiry_date']?.mandatory}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <FormCheckbox
              background={background}
              checked={showEmergencyContact}
              label='Contacto de emergencia'
              onChange={handleEmergencyContactChange}
            />
          </Grid>

          {showEmergencyContact && (
            <EmergencyContactSection
              ref={emergencyContactRef}
              index={index}
              initialValues={passenger?.emergencyContact}
              updateEmergencyContact={updateEmergencyContact}
              showEmergencyContact={showEmergencyContact}
              disabled={false}
            />
          )}

          <FiscalDataSection
            background={background}
            commonFiscalData={commonFiscalData}
            commonFiscalDataPassengerIndex={commonFiscalDataPassengerIndex}
            countryCode={flightInfo?.originCountryCode}
            handleCommonFiscalDataChange={handleCommonFiscalDataChange}
            handleIndividualFiscalDataChange={handleIndividualFiscalDataChange}
            index={index}
            initialValues={passengersFiscalData[index]}
            isInternational={isInternational}
            onToggleCommonData={handleCommonFiscalDataToggle}
            totalPassengers={totalPassengers}
            updateFiscalData={updateFiscalData}
            useCommonFiscalData={useCommonFiscalData}
          />
        </Grid>
      </Box>
    );
  }
);

PassengerForm.displayName = 'PassengerForm';

export default memo(PassengerForm);
